import { ApiFehler } from './api';

/**
 * PB-3 (Codex-Nachprüfung, MITTEL): Abmelden, Bedienerwechsel und automatische Abmeldung
 * widerrufen das Bediener-Kennzeichen am Server. Erreicht der Widerruf den Server nicht
 * (Netzfehler, Zeitüberschreitung, 5xx, 401/429 bei Gerätesperre), bliebe das Kennzeichen bis zum
 * Ablauf (höchstens eine Schicht) am Server gültig. Darum wird jeder Widerruf im Gerätespeicher
 * vorgemerkt und nachgesendet – sofort, beim Start der App, bei Wiederverbindung (Ereignis
 * „online“) und regelmäßig –, bis der Server antwortet (`abgemeldet` true oder false: beides
 * bestätigt, dass das Kennzeichen am Server nicht mehr gilt bzw. nie galt) oder es abläuft.
 *
 * Ablage (Codex Runde 2): ein Schlüssel je Widerruf (`bilanx.bediener.widerruf:<Kasse>:<Kennzeichen>`)
 * statt einer gemeinsamen Liste – Vormerken und Entfernen ändern nur den eigenen Eintrag, kein
 * Lesen-Ändern-Schreiben; parallele Fenster derselben Herkunft verlieren so keine Einträge
 * (doppeltes Senden ist unschädlich, der Widerruf ist idempotent). Keine Obergrenze: Einträge
 * entstehen nur durch Anmeldungen an diesem Gerät und verschwinden mit Bestätigung oder Ablauf
 * (abgelaufene werden physisch entfernt). Jeder neue Eintrag liegt bis zur Bestätigung zusätzlich
 * im Arbeitsspeicher dieser Seite: Scheitert der Gerätespeicher beim Schreiben oder Lesen (voll,
 * gesperrt), wird trotzdem gesendet und wiederholt – nach Schließen der Seite aber nur, was
 * dauerhaft gespeichert werden konnte.
 *
 * Bewusst: Das Kennzeichen liegt bis zur Bestätigung im `localStorage` dieses Geräts (wie der
 * Kassenschlüssel). Es ist an diese Kasse gebunden, läuft spätestens nach einer Schicht ab und
 * wird beim nächsten Start der App sofort widerrufen.
 */

/** Gerätespeicher mit Aufzählung (`localStorage` erfüllt das). */
export interface WiderrufSpeicher {
  getItem(schluessel: string): string | null;
  setItem(schluessel: string, wert: string): void;
  removeItem(schluessel: string): void;
  key(index: number): string | null;
  readonly length: number;
}

/** Ablauf eines nicht lesbaren Kennzeichens: eine Schicht (Server: SCHICHT_MS) ab Vormerkung. */
export const SCHICHT_MS = 10 * 3_600_000;
/** Nachsendung im Hintergrund, solange etwas offen ist. */
const NACHSENDUNG_MS = 60_000;

export interface OffenerWiderruf {
  sitzung: string;
  /** Ablauf (ms seit Epoche); danach ist das Kennzeichen am Server ohnehin ungültig. */
  bis: number;
}

export type Abmelder = (kassenId: string, sitzung: string) => Promise<unknown>;

function praefix(kassenId: string): string {
  return `bilanx.bediener.widerruf:${encodeURIComponent(kassenId)}:`;
}

/** Schlüssel je Kennzeichen (das ganze Kennzeichen – eindeutig, ohne Kollision). */
function schluessel(kassenId: string, sitzung: string): string {
  return `${praefix(kassenId)}${sitzung}`;
}

/** Ablauf aus dem Kennzeichen lesen (base64url-JSON vor dem Punkt); sonst null. */
function ablaufVon(sitzung: string): number | null {
  try {
    const teil = sitzung.split('.')[0] ?? '';
    const b64 = teil.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(
      decodeURIComponent(
        Array.from(atob(b64), (c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''),
      ),
    ) as unknown;
    const bis = (json as { bis?: unknown } | null)?.bis;
    return typeof bis === 'number' && Number.isFinite(bis) ? bis : null;
  } catch {
    return null;
  }
}

/** Einträge, die der Gerätespeicher nicht aufnehmen konnte (je Kasse, nach Schlüssel). */
const ersatz = new Map<string, Map<string, OffenerWiderruf>>();

function ersatzFuer(kassenId: string): Map<string, OffenerWiderruf> {
  let m = ersatz.get(kassenId);
  if (m === undefined) {
    m = new Map();
    ersatz.set(kassenId, m);
  }
  return m;
}

function alsWiderruf(roh: string | null): OffenerWiderruf | null {
  try {
    const x = JSON.parse(roh ?? '') as Partial<OffenerWiderruf> | null;
    return typeof x?.sitzung === 'string' && typeof x.bis === 'number'
      ? { sitzung: x.sitzung, bis: x.bis }
      : null;
  } catch {
    return null;
  }
}

function entferneSchluessel(speicher: WiderrufSpeicher, kassenId: string, k: string): void {
  ersatz.get(kassenId)?.delete(k);
  try {
    speicher.removeItem(k);
  } catch {
    // Speicher gesperrt: Eintrag läuft ab
  }
}

/**
 * Offene (noch nicht bestätigte, nicht abgelaufene) Widerrufe dieser Kasse; abgelaufene und
 * unlesbare Einträge werden dabei entfernt.
 */
export function offeneWiderrufe(
  speicher: WiderrufSpeicher,
  kassenId: string,
  jetzt: number = Date.now(),
): OffenerWiderruf[] {
  const p = praefix(kassenId);
  const gefunden = new Map<string, OffenerWiderruf | null>();
  try {
    const schluesselListe: string[] = [];
    for (let i = 0; i < speicher.length; i++) {
      const k = speicher.key(i);
      if (k?.startsWith(p) === true) {
        schluesselListe.push(k);
      }
    }
    for (const k of schluesselListe) {
      gefunden.set(k, alsWiderruf(speicher.getItem(k)));
    }
  } catch {
    // Speicher nicht lesbar: nur der Arbeitsspeicher
  }
  for (const [k, w] of ersatz.get(kassenId) ?? []) {
    gefunden.set(k, w);
  }
  const offen: OffenerWiderruf[] = [];
  for (const [k, w] of gefunden) {
    if (w === null || w.bis <= jetzt) {
      entferneSchluessel(speicher, kassenId, k);
    } else {
      offen.push(w);
    }
  }
  return offen.sort((a, b) => a.bis - b.bis);
}

function merke(speicher: WiderrufSpeicher, kassenId: string, sitzung: string, jetzt: number) {
  const w: OffenerWiderruf = { sitzung, bis: ablaufVon(sitzung) ?? jetzt + SCHICHT_MS };
  const k = schluessel(kassenId, sitzung);
  // Bis zur Bestätigung immer auch im Arbeitsspeicher (Codex R2): Scheitert später das Lesen des
  // Gerätespeichers, wird trotzdem gesendet und wiederholt.
  ersatzFuer(kassenId).set(k, w);
  try {
    speicher.setItem(k, JSON.stringify(w));
  } catch {
    // Speicher voll/gesperrt: nur der Arbeitsspeicher (geht beim Schließen der Seite verloren)
  }
}

/**
 * Endgültig gescheitert (Wiederholen hilft nicht): Eingabe ungültig (400) oder Kasse unbekannt
 * (404). Alles andere – Netzfehler/Zeitüberschreitung (0), 401/429 (Gerätesperre), 408, 409
 * (App-Version), 5xx und unbekannte Fehler – gilt als vorübergehend.
 */
function endgueltig(e: unknown): boolean {
  return (
    e instanceof ApiFehler &&
    e.status >= 400 &&
    e.status < 500 &&
    ![401, 408, 409, 429].includes(e.status)
  );
}

const laufend = new Set<string>();
const nochmal = new Set<string>();

/**
 * Offene Widerrufe dieser Kasse senden (nacheinander; beim ersten vorübergehenden Fehler
 * Schluss bis zum nächsten Anlass). Liefert die Zahl der danach noch offenen Widerrufe.
 */
export async function sendeWiderrufe(
  abmelden: Abmelder,
  speicher: WiderrufSpeicher,
  kassenId: string,
  jetzt: () => number = Date.now,
): Promise<number> {
  if (laufend.has(kassenId)) {
    nochmal.add(kassenId); // neu Vorgemerktes nach dem laufenden Durchgang senden
    return offeneWiderrufe(speicher, kassenId, jetzt()).length;
  }
  laufend.add(kassenId);
  try {
    do {
      nochmal.delete(kassenId);
      for (const w of offeneWiderrufe(speicher, kassenId, jetzt())) {
        try {
          await abmelden(kassenId, w.sitzung);
        } catch (e) {
          if (!endgueltig(e)) {
            break;
          }
        }
        entferneSchluessel(speicher, kassenId, schluessel(kassenId, w.sitzung));
      }
    } while (nochmal.has(kassenId));
  } finally {
    laufend.delete(kassenId);
  }
  return offeneWiderrufe(speicher, kassenId, jetzt()).length;
}

/** Widerruf vormerken und sofort senden (wirft nie; Melder der App). */
export async function meldeWiderruf(
  abmelden: Abmelder,
  speicher: WiderrufSpeicher,
  kassenId: string,
  sitzung: string,
  jetzt: () => number = Date.now,
): Promise<void> {
  merke(speicher, kassenId, sitzung, jetzt());
  try {
    await sendeWiderrufe(abmelden, speicher, kassenId, jetzt);
  } catch {
    // bleibt vorgemerkt
  }
}

/**
 * Nachsendung starten: sofort (App-Start), bei „online“ und alle `intervallMs`, solange etwas
 * offen ist. Liefert die Aufräumfunktion für den React-Effekt.
 */
export function starteWiderrufNachsendung(
  abmelden: Abmelder,
  speicher: WiderrufSpeicher,
  kassenId: string,
  intervallMs: number = NACHSENDUNG_MS,
): () => void {
  const senden = (): void => {
    if (offeneWiderrufe(speicher, kassenId).length > 0) {
      sendeWiderrufe(abmelden, speicher, kassenId).catch(() => undefined);
    }
  };
  senden();
  window.addEventListener('online', senden);
  const takt = setInterval(senden, intervallMs);
  return () => {
    window.removeEventListener('online', senden);
    clearInterval(takt);
  };
}
